import React, { useState, useContext, useEffect, useCallback } from "react";
import "@/assets/styles/auth-shell.css";
import "./PublicAccess.css";
import Alerts from "@shared/components/Alerts";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { BrandingContext } from "@shared/branding/BrandingContext";

// Deliberately kept on fetch() (not the shared http client): the backend
// answers 400/404/403 with a {message} body that this flow parses and shows
// to the user; axios would throw on those statuses and lose the message.
// The locale is also i18n.language verbatim (possibly "de-AT"), not the
// stripped getCurrentLanguage() the :locale placeholder would inject.
async function getProgramByCode(programCode, lastName, currentLanguage) {
  const params = {
    program_code: programCode,
    last_name: lastName,
  };

  return fetch(
    process.env.REACT_APP_SOPHYAPP_API_URL + `/${currentLanguage}/react/public/programs/get_by_code`,
    {
      crossDomain: true,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(params),
    }
  ).then((data) => data.json());
}

export default function PublicAccess() {
  const { applicationImagePath } = useContext(BrandingContext);
  const [programCode, setProgramCode] = useState("");
  const [lastName, setLastName] = useState("");
  const [loading, setLoading] = useState(false);
  // removed unused error/success states
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("error");

  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleDirectAccess = useCallback(async (code, lastname) => {
    setLoading(true);

    if (!code.trim() || !lastname.trim()) {
      showAlert(t("public-access.invalid-code"), "error");
      setLoading(false);
      return;
    }

    if (code.length < 12) {
      showAlert(t("public-access.code-length"), "error");
      setLoading(false);
      return;
    }

    try {
      const response = await getProgramByCode(code.trim(), lastname.trim(), i18n.language);
      
      if (response.message) {
        showAlert(response.message, "error");
      } else {
        showAlert(t("public-access.program-found"), "success");
        // Navigate to program view with the program data
        navigate("/public-program", { state: { programData: response, programCode: code.trim().toUpperCase(), lastName: lastname.trim() } });
      }
    } catch (err) {
      showAlert(t("public-access.error-occured"), "error");
    } finally {
      setLoading(false);
    }
  }, [i18n.language, navigate, t]);

  // Check for URL parameters on component mount
  useEffect(() => {
    const codeParam = searchParams.get("program_code");
    const lastNameParam = searchParams.get("last_name");
    if (codeParam && lastNameParam) {
      setProgramCode(codeParam.toUpperCase());
      setLastName(lastNameParam);
      // Automatically trigger program lookup
      handleDirectAccess(codeParam, lastNameParam);
    }
  }, [searchParams, handleDirectAccess]);

  const handleAlertClose = () => {
    setAlertOpen(false);
  };

  const showAlert = (message, severity) => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setAlertOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!programCode.trim() || !lastName.trim()) {
      showAlert(t("public-access.invalid-code"), "error");
      setLoading(false);
      return;
    }

    if (programCode.length < 12) {
      showAlert(t("public-access.code-length"), "error");
      setLoading(false);
      return;
    }

    try {
      const response = await getProgramByCode(programCode.trim(), lastName.trim(), i18n.language);
      
      if (response.message) {
        showAlert(response.message, "error");
      } else {
        showAlert(t("public-access.program-found"), "success");
        // Navigate to program view with the program data
        navigate("/public-program", { state: { programData: response, programCode: programCode.trim().toUpperCase(), lastName: lastName.trim() } });
      }
    } catch (err) {
      showAlert(t("public-access.error-occured"), "error");
    } finally {
      setLoading(false);
    }
  };

  /* The same shell as the login: same page background, same 400px card, same
     logo and footer placement — see auth-shell.css, which both screens share
     so they cannot drift apart again. */
  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-box">
          <img
            src={applicationImagePath}
            alt="sophyapp logo"
            className="auth-logo"
          />

          <div className="public-access-header">
            <h2>{t("public-access.title")}</h2>
            <p>{t("public-access.description")}</p>
          </div>

          <Alerts
            open={alertOpen}
            message={alertMessage}
            severity={alertSeverity}
            onClose={handleAlertClose}
          />

          <form onSubmit={handleSubmit}>
            <div className="public-access-field-wrapper">
              <input
                type="text"
                placeholder={t("public-access.program-code")}
                className="input-field-public-access"
                value={programCode}
                onChange={(e) => setProgramCode(e.target.value.toUpperCase())}
                maxLength="16"
                autoFocus
                disabled={loading}
              />
              <input
                type="text"
                placeholder={t("public-access.last-name")}
                className="input-field-public-access"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              className="public-access-button"
              disabled={loading}
            >
              {loading
                ? t("public-access.accessing")
                : t("public-access.access-program")}
            </button>
          </form>

          <div className="public-access-info">
            <p>
              <strong>{t("public-access.need-help")}</strong>
              <br />
              {t("public-access.contact-the-therapist")}
            </p>
          </div>

          <div className="back-to-login">
            <span
              style={{ cursor: "pointer" }}
              className="back-to-login-link"
              onClick={() => navigate("/")}
            >
              {t("public-access.back-login")}
            </span>
          </div>
        </div>
      </div>

      <div className="auth-footer">
        © hoferdigital gmbh &bull;{" "}
        <a
          href="https://www.hoferdigital.at/impressum-datenschutz/"
          target="_blank"
          rel="noopener noreferrer"
        >
          {t("others.privacy")}
        </a>
      </div>
    </div>
  );
} 